import React, { useEffect, useState, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useElements, useStripe, CardNumberElement } from '@stripe/react-stripe-js';
import { StudyContext, BookingContext } from 'packages/booking-contexts';
import { Button, LoadingSpinner, Section, TextInput } from 'shared-library';
import CheckoutForm from './CheckoutForm';
import { StripeElementOnly } from './PaymentTextInputs';



// PaymentIntent-based checkout form
const StripePaymentIntentForm = ({ openAlert, onChange, clientSecret }) => {
  const elements = useElements();
  const stripe = useStripe();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret || !cardholderName.trim()) {
      return;
    }

    setIsProcessing(true);
    try {
      // First, check the current status of the payment intent
      const { paymentIntent: currentIntent } = await stripe.retrievePaymentIntent(clientSecret);
      
      if (currentIntent.status === 'requires_capture') {
        // Payment is already authorized, just needs capture
        // The backend will handle the capture
        onChange?.(currentIntent.status, new Date().toISOString(), currentIntent);
        return;
      }
      
      if (currentIntent.status === 'succeeded') {
        // Payment is already complete
        onChange?.(currentIntent.status, new Date().toISOString(), currentIntent);
        return;
      }

      // Only proceed with confirmCardPayment if the payment intent is in a state that allows it
      if (currentIntent.status === 'requires_payment_method' || currentIntent.status === 'requires_source') {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          openAlert('Payment Error', 'Card information is incomplete');
          return;
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
              }
            }
          }
        );

        if (error) {
          console.error('Stripe confirmCardPayment error:', error);
          openAlert('Payment Error', error.message || 'Unknown Stripe error');
          return;
        }
        
        console.log('Stripe confirmCardPayment result:', { error, paymentIntent });
        
        if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
          onChange?.(paymentIntent.status, new Date().toISOString(), paymentIntent);
        } else if (paymentIntent) {
          console.log('Payment intent in unexpected state after confirmation:', paymentIntent.status);
          openAlert('Payment Error', `Payment confirmation resulted in unexpected state: ${paymentIntent.status}`);
        }
      } else {
        openAlert('Payment Error', `Payment is in unexpected state: ${currentIntent.status}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Section>
        <TextInput
          label="Name on Card"
          value={cardholderName}
          onChange={(value) => setCardholderName(value)}
          placeholder="John Smith"
          required
          maxLength={36}
        />
        <div style={{ marginTop: '16px' }}>
          <StripeElementOnly />
        </div>
        <Button
          type="submit"
          disabled={isProcessing || !stripe || !elements || !cardholderName.trim()}
          variant="primary"
          size="small"
        >
          {isProcessing ? <LoadingSpinner color="white" size="small" /> : 'Pay Now'}
        </Button>
      </Section>
    </form>
  );
};

const StripeElement = ({ show, openAlert, transaction, locationID, onChange }) => {
  const { stripeTokens } = useContext(BookingContext);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const stripeToken = stripeTokens[locationID];
    if (stripeToken && !stripePromise) {
      loadStripe(stripeToken).then(setStripePromise);
    }
  }, [stripeTokens]);

  if (!show || !stripePromise) return null;

  const hasClientSecret = Boolean(transaction.clientSecret);

  
  const elementsOptions = hasClientSecret ? {
    clientSecret: transaction.clientSecret,
    appearance: { theme: 'stripe' },
  } : undefined;

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      {hasClientSecret ? (
        <StripePaymentIntentForm
          openAlert={openAlert}
          onChange={onChange}
          clientSecret={transaction.clientSecret}
        />
      ) : (
        <CheckoutForm
          openAlert={openAlert}
          transaction={transaction}
          onChange={onChange}
        />
      )}
    </Elements>
  );
};

const StripeElements = ({ openAlert, transaction, onChange }) => {
  const { locations } = useContext(BookingContext);
  const { selectedLocation } = useContext(StudyContext);

  return locations.map(({ locationID }) => (
    <StripeElement
      key={`${locationID}-stripe-element`}
      show={locationID === selectedLocation.locationID}
      openAlert={openAlert}
      transaction={transaction}
      locationID={locationID}
      onChange={onChange}
    />
  ));
};

export default StripeElements;
